import {
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Grid,
  LinearProgress,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useReducer } from "react";
import { Redirect } from "react-router";
import useText from "../../hooks/useText";

type TestState = {
  loading: boolean;
  complete: boolean;
  questionsComplete: boolean;
  question: {
    id: number;
    leftTranslation: string;
    rightTranslation: string;
  };
  questionsAnswered: number;
  totalQuestions: number;
  value: "" | "left" | "right" | "0" | "1" | "2" | "3" | "4" | "5";
  redirect: boolean;
  // tiebreaker data
  tiebreakers: { id: number; translation: string }[][];
  currentTiebreakers: { id: number; translation: string }[][];
  currentResolvedTiebreakers: { id: number; translation: string }[][];
  resolvedTiebreakers: number[][];
};

interface Api {
  getNextQuestion: (questionData: any) => Promise<any>;
  getTieBreakers: () => Promise<any>;
  complete: (data: any) => Promise<any>;
}

interface TestProps {
  api: Api;
  redirectLink?: string;
}

type TestAction =
  | {
      type: "setLoading";
    }
  | {
      type: "cancelLoading";
    }
  | {
      type: "setTiebreakers";
      payload: TestState["tiebreakers"];
    }
  | {
      type: "updateTiebreaker";
    }
  | {
      type: "setCompleting";
    }
  | {
      type: "setComplete";
    }
  | {
      type: "setQuestion";
      payload: Partial<TestState>;
    }
  | {
      type: "setValue";
      payload: TestState["value"];
    };

const testReducer: (state: TestState, action: TestAction) => TestState = (
  state,
  action
) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, loading: true };
    case "cancelLoading":
      return { ...state, loading: false };
    case "setTiebreakers": {
      const tiebreakers = action.payload;
      const complete = !tiebreakers.length;
      const currentTiebreakers = complete
        ? []
        : tiebreakers.shift()!.map((a) => [a]);
      return {
        ...state,
        tiebreakers,
        complete,
        currentTiebreakers,
        currentResolvedTiebreakers: [[]],
        value: "",
        loading: false,
      };
    }
    case "updateTiebreaker": {
      /* User Driven Merge Sort :D
              1. Shift the smaller current tiebreaker between [0] and [1] on currentTiebreakers onto currentResolved last array
              2. If [0] or [1] is now empty,
                a. Tack on remaining from other onto end of currentResolved.
                b. Remove [0] and [1].
                c. if current tie breakers is not empty
                  - push a new empty array onto current resolved tiebreakers
                d. if current tie breakers is empty and resolved tiebreakers has more than 1 item
                  - set current tie breakers to current resolved tiebreakers.
                  - set current resolved tiebreakers to [[]]
                e. if current tiebreakers is empty, resolved has 1 item
                  - add current resolved tiebreaker item to resolved tiebreakers array, reversed. That way, highest is first, lowest is last
                  - pop off a tiebreaker from tiebreakers and put it in current tiebreakers
                    + if empty, set complete to true
            */
      const copy = { ...state };
      const index = state.value === "right" ? 0 : 1;
      copy.value = "";
      const currentResolvedIndex = copy.currentResolvedTiebreakers.length - 1;
      copy.currentResolvedTiebreakers[currentResolvedIndex].push(
        copy.currentTiebreakers[index].shift()!
      );
      if (!copy.currentTiebreakers[index].length) {
        copy.currentResolvedTiebreakers[currentResolvedIndex] = [
          ...copy.currentResolvedTiebreakers[currentResolvedIndex],
          ...copy.currentTiebreakers.shift()!,
          ...copy.currentTiebreakers.shift()!,
        ];
        if (copy.currentTiebreakers.length === 1)
          copy.currentResolvedTiebreakers.push(
            copy.currentTiebreakers.shift()!
          );
        if (!copy.currentTiebreakers.length)
          if (copy.currentResolvedTiebreakers.length > 1) {
            copy.currentTiebreakers = copy.currentResolvedTiebreakers;
            copy.currentResolvedTiebreakers = [[]];
          } else {
            copy.resolvedTiebreakers.push(
              copy.currentResolvedTiebreakers[0].map((a) => a.id).reverse()
            );
            copy.currentResolvedTiebreakers = [[]];
            if (!copy.tiebreakers.length) copy.complete = true;
            else
              copy.currentTiebreakers = copy.tiebreakers
                .shift()!
                .map((a) => [a]);
          }
      }
      return copy;
    }
    case "setCompleting":
      return { ...state, loading: true, complete: true };
    case "setComplete":
      return { ...state, loading: false, redirect: true };
    case "setQuestion":
      return { ...state, ...action.payload, loading: false, value: "" };
    case "setValue":
      return { ...state, value: action.payload };
    default:
      return state;
  }
};

const Test: React.FC<TestProps> = ({ api, redirectLink }) => {
  const [testData, testDispatch] = useReducer(testReducer, {
    complete: false,
    loading: false,
    questionsComplete: false,
    question: {
      id: -1,
      leftTranslation: "",
      rightTranslation: "",
    },
    questionsAnswered: 0,
    totalQuestions: 0,
    value: "",
    redirect: false,
    // tiebreaker data
    tiebreakers: [],
    currentTiebreakers: [],
    currentResolvedTiebreakers: [[]],
    resolvedTiebreakers: [],
  } satisfies TestState);

  const {
    lang,
    test: { index: text },
  } = useText()!;

  const getNextQuestion = useCallback(
    async (
      includeCounts: boolean,
      reply?: {
        QuestionId: number;
        value: TestState["value"];
      }
    ) => {
      testDispatch({ type: "setLoading" });
      try {
        const q = await api.getNextQuestion({
          reply,
          includeCounts,
          LanguageId: lang,
        });
        if (!q.question) {
          const tiebreakers = await api.getTieBreakers();
          testDispatch({ type: "setTiebreakers", payload: tiebreakers });
        } else testDispatch({ type: "setQuestion", payload: q });
      } catch (e) {
        console.log(e);
        alert(e);
        testDispatch({ type: "cancelLoading" });
      }
    },
    [api, lang]
  );

  useEffect(() => {
    console.log("useEffect");
    getNextQuestion(true);
  }, [getNextQuestion]);

  useEffect(() => {
    if (testData.complete && !testData.loading && !testData.redirect) {
      testDispatch({ type: "setCompleting" });
      api.complete({ tiebreakers: testData.resolvedTiebreakers }).then(() => {
        testDispatch({ type: "setComplete" });
      });
    }
  }, [testData.complete, api]);

  const onNext = async () => {
    const {
      question: { id: QuestionId },
      value,
    } = testData;
    console.log("onNext", QuestionId, value);
    await getNextQuestion(false, { QuestionId, value });
  };

  const onTiebreakerNext = () => {
    testDispatch({ type: "updateTiebreaker" });
  };

  const theme = useTheme();
  const useRow = !useMediaQuery(theme.breakpoints.down("sm"));
  if (testData.redirect && redirectLink) return <Redirect to={redirectLink} />;
  return (
    <>
      <Card raised>
        <CardContent>
          <LinearProgress
            variant="determinate"
            value={(testData.questionsAnswered / testData.totalQuestions) * 100}
          />
        </CardContent>
        <Typography color="primary" variant="h5" align="center">
          {text.prompt}
        </Typography>
        <Grid container alignItems="center" spacing={2}>
          <Grid
            item
            xs={useRow ? undefined : 12}
            style={{ flex: useRow ? 1 : undefined }}
          >
            <Typography align={useRow ? "right" : "center"} variant="body1">
              {testData.currentTiebreakers.length
                ? testData.currentTiebreakers[0][0].translation
                : testData.question.leftTranslation}
            </Typography>
          </Grid>
          <Grid item xs={useRow ? undefined : 12}>
            <RadioGroup
              value={testData.value}
              onChange={(e) =>
                testDispatch({
                  type: "setValue",
                  payload: e.target.value as TestState["value"],
                })
              }
              row={useRow}
            >
              {testData.currentTiebreakers.length ? (
                <>
                  <Radio color="secondary" value="left" />
                  <Radio color="secondary" value="right" />
                </>
              ) : (
                Array.from(Array(6), (_, i) => (
                  <Radio
                    key={"radio" + i}
                    color="secondary"
                    value={6 - i + ""}
                  />
                ))
              )}
            </RadioGroup>
          </Grid>
          <Grid
            item
            xs={useRow ? undefined : 12}
            style={{ flex: useRow ? 1 : undefined }}
          >
            <Typography align={useRow ? "left" : "center"} variant="body1">
              {testData.currentTiebreakers.length
                ? testData.currentTiebreakers[1][0].translation
                : testData.question.rightTranslation}
            </Typography>
          </Grid>
        </Grid>
        <CardActions>
          <Button
            startIcon={
              testData.loading && <CircularProgress color="primary" size={28} />
            }
            style={{ margin: "auto", textTransform: "none" }}
            variant="contained"
            color="secondary"
            onClick={
              testData.currentTiebreakers.length ? onTiebreakerNext : onNext
            }
            disabled={!testData.value || testData.loading}
          >
            {text.next}
          </Button>
        </CardActions>
      </Card>
    </>
  );
};

export default Test;
